import React, { useCallback, useEffect, useState } from "react";
import { notify } from "../../../../services/toast.config";
import api from "../../../../services/api";
import Loader from "../../../../core/common/loader";
import CommonSelect from "../../../../core/common/commonSelect";

interface EditLocationProps {
  selectedLocation: any;
  updateUI: () => Promise<void>;
  selectedCompany: {
    company_id: string;
    company_name: string;
    company_type: string;
  };
}

type LocationFormData = {
  id: string;
  location_name: string;
  building: string;
  floor: string;
  room: string;
  status: string;
  company_id: string;
  company_type: string;
  company_name: string;
};

const DEFAULT_FORM_DATA: LocationFormData = {
  id: "",
  location_name: "",
  building: "",
  floor: "",
  room: "",
  status: "",
  company_id: "",
  company_type: "",
  company_name: "",
};

const STATUS_OPTIONS = [
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
];

const EditLocation: React.FC<EditLocationProps> = ({
  selectedLocation,
  updateUI,
  selectedCompany,
}) => {
  // Local Storage
  const signature = localStorage.getItem("signature");

  // State
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());
  const [formData, setFormData] = useState<LocationFormData>(DEFAULT_FORM_DATA);

  // Helper Functions
  const resetForm = useCallback(() => {
    if (selectedLocation) {
      setFormData({
        id: selectedLocation.id || "",
        location_name: selectedLocation.location_name || "",
        building: selectedLocation.building || "",
        floor: selectedLocation.floor || "",
        room: selectedLocation.room || "",
        status: selectedLocation.status || "",
        company_id: selectedCompany.company_id || "",
        company_type: selectedCompany.company_type || "",
        company_name: selectedCompany.company_name || "",
      });
      setInvalidFields(new Set());
    }
  }, [selectedLocation]);

  const validateForm = useCallback((): boolean => {
    const requiredFields = [
      { key: "id", label: "Location Id" },
      { key: "location_name", label: "Location Name" },
      { key: "status", label: "Status" },
      { key: "company_id", label: "Company" },
    ];

    const invalid = new Set<string>();
    requiredFields.forEach(({ key }) => {
      if (!formData[key as keyof LocationFormData]?.toString().trim()) {
        invalid.add(key);
      }
    });

    setInvalidFields(invalid);
    return invalid.size === 0;
  }, [formData]);

  const clearFieldError = useCallback((fieldName: string) => {
    setInvalidFields((prev) => {
      const newSet = new Set(prev);
      newSet.delete(fieldName);
      return newSet;
    });
  }, []);

  // Event Handlers
  const handleInputChange = useCallback(
    (key: string, value: string) => {
      clearFieldError(key);
      setFormData((prev) => ({ ...prev, [key]: value }));
    },
    [clearFieldError]
  );

  const handleSubmitLocation = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) return;

    try {
      setIsSubmitting(true);
      setIsLoading(true);

      const dataToSend = {
        signature,
        id: formData.id,
        location_name: formData.location_name.trim(),
        building: formData.building.trim(),
        floor: formData.floor.trim(),
        room: formData.room.trim(),
        status: formData.status === "Active" ? 1 : 0,
        company_id: formData.company_id,
        company_type: formData.company_type,
      };

      const response = await api.post("userService/updateLocation", dataToSend);

      notify.success(
        response.data?.message[0]?.msg || "Location updated successfully."
      );

      // Close modal on success
      const dismissButton = document.querySelector(
        "#dismiss-edit-location-btn"
      ) as HTMLButtonElement;
      if (dismissButton) {
        dismissButton.click();
      }

      await updateUI();
    } catch (error: any) {
      const errorMessage =
        error?.data?.message?.[0]?.msg ||
        error?.data?.message ||
        "An error occurred.";
      notify.error(errorMessage);
    } finally {
      setIsSubmitting(false);
      setIsLoading(false);
    }
  };

  // Initialize form data whenever selectedLocation changes
  useEffect(() => {
    if (selectedLocation) {
      resetForm();
    }
  }, [selectedLocation, resetForm]);

  return (
    <>
      {isLoading && <Loader />}

      <div className="modal fade" id="edit_location">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Edit Location</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                disabled={isSubmitting}>
                <i className="ti ti-x" />
              </button>
            </div>

            <form onSubmit={handleSubmitLocation}>
              <div className="modal-body pb-0">
                <div className="row">
                  {/* Company Display */}
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">
                        Company <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={`${formData.company_name} - ${formData.company_type}`}
                        disabled={true}
                        readOnly
                      />
                      {invalidFields.has("company_id") && (
                        <div className="invalid-feedback d-block">
                          <small className="text-danger">
                            Company is required
                          </small>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Location Name */}
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">
                        Location Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-control ${
                          invalidFields.has("location_name")
                            ? "is-invalid border-danger"
                            : ""
                        }`}
                        value={formData.location_name}
                        onChange={(e) =>
                          handleInputChange("location_name", e.target.value)
                        }
                        placeholder="Enter location name"
                        disabled={isSubmitting}
                        autoFocus
                      />
                      {invalidFields.has("location_name") && (
                        <div className="invalid-feedback d-block">
                          <small className="text-danger">
                            Location Name is required
                          </small>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Building */}
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Building</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.building}
                        onChange={(e) =>
                          handleInputChange("building", e.target.value)
                        }
                        placeholder="Enter building"
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  {/* Floor */}
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Floor</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.floor}
                        onChange={(e) =>
                          handleInputChange("floor", e.target.value)
                        }
                        placeholder="Enter floor"
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  {/* Room */}
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Room</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.room}
                        onChange={(e) =>
                          handleInputChange("room", e.target.value)
                        }
                        placeholder="Enter room"
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  {/* Status */}
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">
                        Status <span className="text-danger">*</span>
                      </label>
                      <div
                        className={
                          invalidFields.has("status")
                            ? "border border-danger rounded"
                            : ""
                        }>
                        <CommonSelect
                          key={`status-${formData.id}-${Date.now()}`}
                          options={STATUS_OPTIONS}
                          defaultValue={
                            formData.status
                              ? {
                                  value: formData.status,
                                  label: formData.status,
                                }
                              : undefined
                          }
                          onChange={(selected: any) =>
                            handleInputChange("status", selected?.value || "")
                          }
                          placeHolder="Select Status"
                          isDisabled={isSubmitting}
                        />
                      </div>
                      {invalidFields.has("status") && (
                        <div className="invalid-feedback d-block">
                          <small className="text-danger">
                            Status is required
                          </small>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                  disabled={isSubmitting}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      Updating...
                    </>
                  ) : (
                    "Update Location"
                  )}
                </button>

                {/* Hidden button for programmatic modal close */}
                <button
                  id="dismiss-edit-location-btn"
                  type="button"
                  className="d-none"
                  data-bs-dismiss="modal"
                />
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditLocation;
